import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup } from 'reactstrap';
import { kebabCase } from 'lodash';
import { parentPath } from '@truqc/content-path';
import { convertToAbsolutePath } from '../utils';
import { enabledSelector, labelSelector, newPathSelector, onNullDependentSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedSegmentedButton } from './segmentedButton';
import Label from './label';
import logValueOnChange from '../commonActions/logValueOnChange';

// must be defined outside of the component to avoid re-rendering loops
const defaultOnBlur = () => {};
const defaultOnFocus = () => {};
const SinglePickerButton = ({
  enabled,
  onBlur: _onBlur = defaultOnBlur,
  onChange,
  path,
  onFocus: _onFocus = defaultOnFocus,
  value: _value = null,
  label: _label = null,
  options: _options = [],
  className: _className = '',
  allowsNull: _allowsNull = true,
  onNull: _onNull = null,
  testingIdentifier: _testingIdentifier = '',
  onNullHasDependentValue: _onNullHasDependentValue = false,
  notificationActions
}) => {
  const handleChange = useCallback(async option => {
    const displayValue = option ? option.identifier : null;
    // If a valid option was selected
    if (!_value || option.identifier !== _value.identifier) {
      return onChange({
        path,
        value: option,
        displayValue
      });
    }
    // If the picker isn't nullable
    if (!_allowsNull) {
      return null;
    }
    // If there's no dependent data or an onNull definition
    if (!_onNull || !_onNullHasDependentValue) {
      return onChange({
        path,
        value: null
      });
    }
    // Otherwise, present the nullWarning modal
    const {
      nullWarning,
      dependentContentPaths = []
    } = _onNull;
    return notificationActions.info({
      message: nullWarning.message,
      headerMessage: nullWarning.title,
      rightButtonLabel: nullWarning.nullTitle,
      rightButtonAction: () => {
        dependentContentPaths.forEach(dependentPath => {
          // Set all the values of dependentContentPaths to null
          const absolutePath = `/${convertToAbsolutePath(dependentPath, parentPath(path))}`;
          onChange({
            path: absolutePath,
            value: null
          });
        });
        onChange({
          path,
          value: null
        });
      },
      leftButtonLabel: nullWarning.cancelTitle,
      id: 'picker-on-null-notification'
    }, {
      autoClose: false
    });
  }, [_allowsNull, notificationActions, onChange, path, _onNull, _onNullHasDependentValue, _value]);
  const activeValue = _value && typeof _value === 'object' ? _value.identifier : _value;
  const newClassName = _label !== null && _label.length ? `t-spb t-spb-${kebabCase(_label)} ${_className}` : _className;
  const testID = _testingIdentifier || `spb-${kebabCase(_label)}`;
  return /*#__PURE__*/React.createElement("div", {
    className: `s-spb ${newClassName} tqc-control `,
    "data-testid": testID
  }, /*#__PURE__*/React.createElement(Label, {
    value: _label
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    style: {
      display: 'flex'
    },
    value: activeValue === 0 ? 0 : activeValue || '',
    onBlur: _onBlur,
    onFocus: _onFocus
  }, _options.map(option => /*#__PURE__*/React.createElement(UncomposedSegmentedButton, {
    enabled: enabled,
    key: `SGBTN_${option.identifier}`,
    active: activeValue === option.identifier,
    onChange: handleChange,
    option: option,
    label: _label
  }))));
};
SinglePickerButton.propTypes = {
  allowsNull: PropTypes.bool,
  className: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onNullHasDependentValue: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired,
  path: PropTypes.string.isRequired,
  onNull: PropTypes.shape({
    dependentContentPaths: PropTypes.arrayOf(PropTypes.string),
    nullWarning: PropTypes.shape({
      title: PropTypes.string,
      message: PropTypes.string,
      cancelTitle: PropTypes.string,
      nullTitle: PropTypes.string
    })
  }),
  testingIdentifier: PropTypes.string,
  value: PropTypes.shape({
    identifier: PropTypes.string
  })
};
export { SinglePickerButton as UncomposedSinglePickerButton };
export default entityComposition(SinglePickerButton, [], {
  enabled: enabledSelector,
  label: labelSelector,
  onNullHasDependentValue: onNullDependentSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
}, ['notification']);